// Entrada manual: o que aparelho nenhum mede — e o que o aparelho deveria ter
// medido, mas nao mediu.
//
// A enfermeira passa três vezes ao dia e afere saturação, temperatura e
// pressão. Se isso não entra no sistema, o histórico fica com buraco justamente
// nos horários em que alguém olhou a paciente de perto.
//
// Tudo que entra por aqui é gravado como `manual` e aparece assim no monitor —
// nunca se confunde com o que veio do aparelho.
import { useEffect, useState, type FormEvent } from 'react';
import {
  FAIXA_PLAUSIVEL, UNIDADE_POR_TIPO, type TipoLeitura, doCampoParaISO, agoraParaCampo,
} from '@medicov1/shared';
import { chamar } from '../api';
import './registrar.css';
// A confirmação de "salvo" é a mesma da tela Anotar: um padrão só para as duas.
import './anotar.css';

/**
 * Tudo que pode ser digitado — inclusive o que normalmente vem por Bluetooth.
 *
 * Glicemia e frequência cardíaca estão aqui porque o aparelho falha: pilha
 * acabando, pareamento perdido, tablet longe. A medição aconteceu de qualquer
 * jeito, e perdê-la por causa do rádio seria o pior dos mundos. Digitado entra
 * como `manual` e o monitor mostra assim — nunca se confunde com o do aparelho.
 */
const CAMPOS: { tipo: TipoLeitura; rotulo: string; passo: number }[] = [
  { tipo: 'glicemia', rotulo: 'Glicemia', passo: 1 },
  { tipo: 'saturacao', rotulo: 'Saturação', passo: 1 },
  { tipo: 'temperatura', rotulo: 'Temperatura', passo: 0.1 },
  { tipo: 'frequencia_respiratoria', rotulo: 'Freq. respiratória', passo: 1 },
  { tipo: 'diurese', rotulo: 'Diurese', passo: 10 },
  { tipo: 'evacuacoes', rotulo: 'Evacuações', passo: 1 },
  { tipo: 'dieta_enteral', rotulo: 'Dieta enteral', passo: 5 },
  { tipo: 'balanco_hidrico', rotulo: 'Balanço hídrico', passo: 10 },
  { tipo: 'peso', rotulo: 'Peso', passo: 0.1 },
];

/**
 * Formato aceito por <input type="datetime-local">, no relógio de BRASÍLIA — não
 * no do celular: a gravação lê o campo como Brasília, então os dois têm de ser o
 * mesmo relógio.
 */
const agoraLocal = () => agoraParaCampo();

interface Props {
  pacienteId: string;
  aoGravar: () => void;
  aoVoltar: () => void;
  /** Sinal que o usuário tocou no monitor: abre com esse campo já no foco. */
  focoEm?: TipoLeitura;
}

/** Qual campo recebe o cursor quando se chega aqui pelo toque num card. */
function campoDoFoco(tipo: TipoLeitura | undefined): string | null {
  if (!tipo) return null;
  if (tipo === 'pa_sistolica' || tipo === 'pa_diastolica') return 'sis';
  if (tipo === 'frequencia_cardiaca') return 'pul';
  return CAMPOS.some((c) => c.tipo === tipo) ? tipo : null;
}

export function Registrar({ pacienteId, aoGravar, aoVoltar, focoEm }: Props) {
  const [quando, setQuando] = useState(agoraLocal());
  const [valores, setValores] = useState<Record<string, string>>({});
  const [pressao, setPressao] = useState({ sistolica: '', diastolica: '', pulso: '' });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [gravadas, setGravadas] = useState(0);

  const trocar = (tipo: string, valor: string) =>
    setValores((v) => ({ ...v, [tipo]: valor }));

  // Chegou pelo toque num card do monitor: o cursor já espera no campo certo,
  // senão a pessoa desce a tela procurando onde digitar com o aparelho na mão.
  const alvo = campoDoFoco(focoEm);
  useEffect(() => {
    if (!alvo) return;
    const campo = document.getElementById(alvo) as HTMLInputElement | null;
    campo?.focus();
    campo?.scrollIntoView({ block: 'center' });
  }, [alvo]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErro('');
    setGravadas(0);

    const medidoEm = doCampoParaISO(quando);
    const aEnviar: { tipo: TipoLeitura; valor: number; grupo?: string }[] = [];

    for (const campo of CAMPOS) {
      const bruto = valores[campo.tipo]?.replace(',', '.').trim();
      if (!bruto) continue;
      const valor = Number(bruto);
      if (!Number.isFinite(valor)) {
        setErro(`${campo.rotulo}: valor inválido.`);
        return;
      }
      const faixa = FAIXA_PLAUSIVEL[campo.tipo];
      if (valor < faixa.min || valor > faixa.max) {
        setErro(`${campo.rotulo}: ${valor} está fora do possível (${faixa.min}–${faixa.max}). Confira o que digitou.`);
        return;
      }
      aEnviar.push({ tipo: campo.tipo, valor });
    }

    // Pressão vira três leituras do mesmo evento, unidas pelo grupo — igual ao
    // que o monitor de braço manda por Bluetooth.
    const temPa = pressao.sistolica || pressao.diastolica;
    if (temPa && (!pressao.sistolica || !pressao.diastolica)) {
      setErro('Pressão: preencha sistólica e diastólica.');
      return;
    }

    if (temPa) {
      const grupo = `manual-${Date.now()}`;
      aEnviar.push({ tipo: 'pa_sistolica', valor: Number(pressao.sistolica), grupo });
      aEnviar.push({ tipo: 'pa_diastolica', valor: Number(pressao.diastolica), grupo });
      if (pressao.pulso) aEnviar.push({ tipo: 'frequencia_cardiaca', valor: Number(pressao.pulso), grupo });
    } else if (pressao.pulso) {
      // Pulso sozinho e medida legitima: a pulseira falhou e alguem contou no
      // pulso, ou leu no visor do oximetro. Exigir a pressao junto faria perder
      // o dado justamente quando o aparelho deu problema.
      aEnviar.push({ tipo: 'frequencia_cardiaca', valor: Number(pressao.pulso) });
    }

    if (!aEnviar.length) {
      setErro('Preencha ao menos um campo.');
      return;
    }

    setEnviando(true);
    try {
      for (const item of aEnviar) {
        await chamar('/leituras', {
          metodo: 'POST',
          corpo: { pacienteId, origem: 'manual', medidoEm, ...item },
        });
      }
      setGravadas(aEnviar.length);
      setValores({});
      setPressao({ sistolica: '', diastolica: '', pulso: '' });
      setQuando(agoraLocal());
      aoGravar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
    } finally {
      setEnviando(false);
    }
  }

  if (gravadas > 0) {
    return (
      <main className="registrar">
        <header className="registrar__topo"><h1>Registrar medida</h1></header>
        <section className="salvo">
          <div className="salvo__marca" aria-hidden="true">✓</div>
          <h2>{gravadas === 1 ? 'Medida gravada' : `${gravadas} medidas gravadas`}</h2>
          <p className="salvo__resumo">Já aparecem no monitor e na linha do tempo.</p>
          <div className="salvo__acoes">
            <button className="botao" onClick={() => setGravadas(0)}>Registrar outra</button>
            <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="registrar">
      <header className="registrar__topo">
        <h1>Registrar medida</h1>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      <p className="registrar__nota">
        Para o que o aparelho não mede — e para quando ele falha. Fica marcado como{' '}
        <b>digitado à mão</b> no monitor, com a hora em que a medida aconteceu, não a
        hora em que você digitou.
      </p>

      <form onSubmit={enviar} className="registrar__form">
        <div className="campo registrar__quando">
          <label htmlFor="quando">Hora da medida</label>
          <input id="quando" type="datetime-local" value={quando}
            onChange={(e) => setQuando(e.target.value)} required />
        </div>

        <fieldset className="registrar__grupo">
          <legend>Pressão arterial</legend>
          <div className="registrar__pa">
            <div className="campo">
              <label htmlFor="sis">Sistólica</label>
              <input id="sis" type="number" inputMode="numeric" placeholder="115"
                value={pressao.sistolica} onChange={(e) => setPressao({ ...pressao, sistolica: e.target.value })} />
            </div>
            <div className="campo">
              <label htmlFor="dia">Diastólica</label>
              <input id="dia" type="number" inputMode="numeric" placeholder="75"
                value={pressao.diastolica} onChange={(e) => setPressao({ ...pressao, diastolica: e.target.value })} />
            </div>
            <div className="campo">
              <label htmlFor="pul">Pulso <span>vale sozinho</span></label>
              <input id="pul" type="number" inputMode="numeric" placeholder="96"
                value={pressao.pulso} onChange={(e) => setPressao({ ...pressao, pulso: e.target.value })} />
            </div>
          </div>
        </fieldset>

        <fieldset className="registrar__grupo">
          <legend>Demais medidas</legend>
          <div className="registrar__campos">
            {CAMPOS.map((campo) => (
              <div className="campo" key={campo.tipo}>
                <label htmlFor={campo.tipo}>
                  {campo.rotulo} <span>{UNIDADE_POR_TIPO[campo.tipo]}</span>
                </label>
                <input
                  id={campo.tipo}
                  type="number"
                  inputMode="decimal"
                  step={campo.passo}
                  value={valores[campo.tipo] ?? ''}
                  onChange={(e) => trocar(campo.tipo, e.target.value)}
                />
              </div>
            ))}
          </div>
        </fieldset>

        {erro && <p className="aviso-erro">{erro}</p>}

        <div className="registrar__acoes">
          <button className="botao" type="submit" disabled={enviando}>
            {enviando ? 'Gravando…' : 'Gravar'}
          </button>
          <span className="registrar__dica">Preencha só o que foi aferido — campo vazio é ignorado.</span>
        </div>
      </form>
    </main>
  );
}
